import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { X, Search, FileSpreadsheet } from "lucide-react";
import { api, AYLAR, formatTL } from "@/lib/api";
import { LeaveDaysList } from "./LeaveDaysList";

const n1 = (v) => Number(v || 0).toLocaleString("tr-TR", { maximumFractionDigits: 1 });

const KINDS = {
  yemek: {
    title: "Yemek Detayı",
    cols: [
      ["yemek_gun_sayisi", "Gün", n1],
      ["yemek_gunluk_tutar", "Günlük", formatTL],
      ["yemek_ekstra", "Ekstra", formatTL],
      ["yemek_toplam", "Toplam", formatTL],
    ],
    active: (r) => (r.yemek_toplam || 0) > 0 || (r.yemek_gun_sayisi || 0) > 0,
  },
  net: {
    title: "Net Maaş Detayı",
    cols: [
      ["brut_maas", "Brüt Maaş", formatTL],
      ["toplam_kesinti", "Toplam Kesinti", formatTL],
      ["avans_kesintisi", "Avans Kesintisi", formatTL],
      ["net_maas", "Net Maaş", formatTL],
    ],
    active: (r) => r.net_maas == null || r.net_maas !== 0,
    bold: "net_maas",
  },
  avans: {
    title: "Avans Kesintisi Detayı",
    cols: [
      ["avans_kesintisi", "Avans Kesintisi", formatTL],
      ["net_maas", "Net Maaş", formatTL],
    ],
    active: (r) => r.avans_kesintisi == null || r.avans_kesintisi > 0,
    bold: "avans_kesintisi",
  },
  izin: {
    title: "İzin + Rapor Detayı",
    cols: [
      ["ucretli_izin_gun", "Ücretli / Yıllık İzin", n1],
      ["ucretsiz_izin_gun", "Ücretsiz İzin", n1],
      ["raporlu_gun", "Rapor", n1],
      ["_toplam", "Toplam", n1],
    ],
    active: (r) => r._toplam > 0,
  },
};

export function KpiDetailModal({ kind, yil, ay, onClose }) {
  const cfg = KINDS[kind];
  const [rows, setRows] = useState(null);
  const [q, setQ] = useState("");
  const [onlyActive, setOnlyActive] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [openId, setOpenId] = useState(null);
  const [tick, setTick] = useState(0);

  const downloadXlsx = async () => {
    setDownloading(true);
    try {
      const r = await api.get(`/dashboard/detail.xlsx?yil=${yil}&ay=${ay}&kind=${kind}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `${{ yemek: "yemek", izin: "izin_rapor", net: "net_maas", avans: "avans_kesintisi" }[kind]}_detay_${yil}_${String(ay).padStart(2, "0")}.xlsx`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error("Excel indirilemedi");
    } finally {
      setDownloading(false);
    }
  };

  useEffect(() => {
    api.get(`/dashboard?yil=${yil}&ay=${ay}&rows=true`)
      .then((r) => setRows((r.data.rows || []).map((x) => ({
        ...x, _toplam: (x.ucretli_izin_gun || 0) + (x.ucretsiz_izin_gun || 0) + (x.raporlu_gun || 0),
      }))))
      .catch(() => setRows([]));
  }, [yil, ay, tick]);

  const shown = useMemo(() => (rows || []).filter((r) =>
    (!onlyActive || cfg.active(r)) && (r.ad_soyad || "").toLocaleLowerCase("tr").includes(q.toLocaleLowerCase("tr"))), [rows, q, onlyActive, cfg]);
  const totals = cfg.cols.map(([k]) => shown.reduce((s, r) => s + (Number(r[k]) || 0), 0));

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/50 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose} data-testid={`kpi-detail-modal-${kind}`}>
      <div className="bg-white rounded-xl shadow-xl w-full max-w-4xl max-h-[85vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-4 border-b border-slate-200 flex items-center gap-3">
          <h3 className="font-bold text-slate-900">{cfg.title} · {AYLAR[ay - 1].n} {yil}</h3>
          {kind === "izin" && <span className="text-[11px] text-slate-500">Kişiye tıklayın: gün gün döküm</span>}
          <button onClick={downloadXlsx} disabled={downloading} data-testid="kpi-detail-excel-btn"
            className="ml-auto inline-flex items-center gap-1.5 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white text-xs font-semibold px-3 py-1.5 rounded-md transition-colors">
            <FileSpreadsheet size={14} /> {downloading ? "Hazırlanıyor..." : "Excel'e Aktar"}
          </button>
          <button onClick={onClose} className="w-8 h-8 grid place-items-center rounded-lg hover:bg-slate-100" data-testid="kpi-detail-close"><X size={16} /></button>
        </div>
        <div className="px-5 py-3 flex items-center gap-3 flex-wrap border-b border-slate-100">
          <div className="relative flex-1 min-w-[200px]">
            <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Personel ara..." data-testid="kpi-detail-search"
              className="w-full pl-8 pr-3 py-1.5 border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-slate-900/10" />
          </div>
          <label className="flex items-center gap-1.5 text-xs text-slate-700 cursor-pointer">
            <input type="checkbox" checked={onlyActive} onChange={(e) => setOnlyActive(e.target.checked)} data-testid="kpi-detail-only-active" />
            Sadece değeri olanlar
          </label>
        </div>
        <div className="overflow-auto flex-1">
          <table className="w-full text-sm" data-testid="kpi-detail-table">
            <thead className="bg-slate-50 sticky top-0">
              <tr className="text-slate-600 text-xs">
                <th className="text-left px-3 py-2 font-bold">Personel</th>
                <th className="text-left px-3 py-2 font-bold">Pozisyon</th>
                {cfg.cols.map(([k, l]) => <th key={k} className="text-right px-3 py-2 font-bold whitespace-nowrap">{l}</th>)}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {rows === null && <tr><td colSpan={cfg.cols.length + 2} className="text-center text-slate-400 py-8">Yükleniyor...</td></tr>}
              {rows !== null && shown.length === 0 && <tr><td colSpan={cfg.cols.length + 2} className="text-center text-slate-400 py-8">Kayıt yok</td></tr>}
              {shown.map((r) => (
                <React.Fragment key={r.personnel_id}>
                <tr className={`hover:bg-slate-50 ${kind === "izin" ? "cursor-pointer" : ""} ${openId === r.personnel_id ? "bg-slate-50" : ""}`}
                  onClick={() => kind === "izin" && setOpenId(openId === r.personnel_id ? null : r.personnel_id)}
                  title={kind === "izin" ? "Gün gün izin dökümü için tıklayın" : undefined} data-testid={`kpi-detail-row-${r.personnel_id}`}>
                  <td className="px-3 py-2 font-semibold text-slate-900">{r.ad_soyad}<span className="text-[10px] text-slate-400 ml-1">{r.sicil_no}</span></td>
                  <td className="px-3 py-2 text-xs text-slate-600">{r.pozisyon || "-"}</td>
                  {cfg.cols.map(([k, , f]) => (
                    <td key={k} className={`px-3 py-2 text-right font-mono-tab whitespace-nowrap ${k === "_toplam" || k === "yemek_toplam" || k === cfg.bold ? "font-bold text-slate-900" : "text-slate-700"}`}>
                      {r[k] == null ? "•••" : f(r[k])}
                    </td>
                  ))}
                </tr>
                {kind === "izin" && openId === r.personnel_id && (
                  <tr><td colSpan={cfg.cols.length + 2} className="px-4 py-3 bg-slate-50 border-l-4 border-slate-300"><LeaveDaysList row={r} yil={yil} ay={ay} onChanged={() => setTick((t) => t + 1)} /></td></tr>
                )}
                </React.Fragment>
              ))}
            </tbody>
            {shown.length > 0 && (
              <tfoot className="bg-slate-100 sticky bottom-0">
                <tr className="font-bold text-slate-900">
                  <td className="px-3 py-2" colSpan={2} data-testid="kpi-detail-count">TOPLAM · {shown.length} personel</td>
                  {cfg.cols.map(([k, , f], i) => (
                    <td key={k} className="px-3 py-2 text-right font-mono-tab whitespace-nowrap" data-testid={`kpi-detail-total-${k}`}>
                      {k === "yemek_gunluk_tutar" ? "" : f(totals[i])}
                    </td>
                  ))}
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>
    </div>
  );
}
